/**
 * Hospital edge node health over a window: availability, reboots, the
 * replication backlog and the disk — the Health tab's edge half.
 *
 * It replaces the two raw node charts the Health tab used to draw straight
 * from heartbeat history ("Unreplicated studies per node", "Node disk"). Those
 * plotted every stored heartbeat, kept 30 days, capped at 5 000 points and
 * spaced unevenly by design; this reads the per-day rows the cloud ingest
 * keeps for every accepted beat (`edge_node_daily`), so it covers any window,
 * buckets by Baghdad day like every other report, and can say "availability".
 *
 * What the words mean, because each is easy to over-read:
 *
 * * **Availability is as seen by the cloud ingest** — the share of the time a
 *   node existed in which its heartbeats were arriving. A hospital whose
 *   internet is down but whose node is fine reads as unavailable here, and
 *   that is the right reading for "can the cloud hear it".
 * * **Before a node's record began is not downtime.** Those days are null and
 *   draw nothing; the table says from when each node is recorded.
 * * **The disk forecast is a straight line** through the lowest free disk of
 *   each of the last 30 days. It needs at least 7 of them, and it cannot know
 *   that Orthanc's own storage cap may start recycling old studies first.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Callout } from '../Callout';
import { TableScroll } from '../SortableTable';
import { ErrorBlock, LoadingBlock } from '../States';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { DownloadIcon } from '../ui/Icon';
import { ChartFrame, LineChart } from '../viz/Chart';
import type { Series } from '../viz/Chart';
import { ApiError } from '../../lib/api';
import { downloadCsv } from '../../lib/csv';
import type { CsvCell } from '../../lib/csv';
import { formatBytes, formatDateTime, formatNumber, formatRelative, UNKNOWN } from '../../lib/format';
import { useResource } from '../../lib/useResource';
import type {
  EdgeHealthPoint,
  EdgeHealthResponse,
  EdgeNodeHealth,
  EdgeNodeResponse,
  UploadBucket,
} from '../../types/api';
import { dayAsInstant, edgeQuery, percent, rangeText } from './edgeReportFormat';

/** Lines a chart keeps; past eight, identity by colour is a guess. */
const MAX_LINES = 8;

/** The status colour for an availability, as the Health tab's ranking uses
 *  it. Reinforcement only — the percentage is always printed beside it. */
function availabilityColor(value: number | null): string | undefined {
  if (value === null) return undefined;
  return value >= 0.99
    ? 'var(--status-good)'
    : value >= 0.95
      ? 'var(--status-warning)'
      : 'var(--status-critical)';
}

function forecastText(node: EdgeNodeHealth): ReactNode {
  const f = node.forecast;
  if (f.days_to_full !== null && f.full_on) {
    return (
      <span title={`${formatNumber(f.points)} daily readings`}>
        full in ~{formatNumber(Math.round(f.days_to_full))} d ({f.full_on})
      </span>
    );
  }
  return <span className="muted">{f.reason || UNKNOWN}</span>;
}

function diskText(free: number | null, pct: number | null): string {
  if (free === null) return UNKNOWN;
  return pct === null ? formatBytes(free) : `${formatBytes(free)} (${pct.toFixed(0)}%)`;
}

export interface EdgeHealthSectionProps {
  /** Inclusive Baghdad days. */
  fromDay: string;
  toDay: string;
  /** Day-grained only; a page on hourly buckets passes `day` and says so. */
  bucket: UploadBucket;
  slug?: string | null;
  node?: string | null;
  /** The live registry rows, when the page already has them: the table's
   *  "now" columns (backlog now, last replication) come from here rather than
   *  from yesterday's daily row. */
  liveNodes?: readonly EdgeNodeResponse[];
  /** Shown under the heading — e.g. why an hourly window is shown by day. */
  note?: ReactNode;
  ready?: boolean;
}

/**
 * The per-node table and three small charts. Returned as a fragment of bento
 * cards, like the rest of the Health tab.
 */
export function EdgeHealthSection({
  fromDay,
  toDay,
  bucket,
  slug = null,
  node = null,
  liveNodes,
  note,
  ready = true,
}: EdgeHealthSectionProps) {
  const report = useResource<EdgeHealthResponse>(
    `/api/reports/edge-health?${edgeQuery(
      { from: fromDay, to: toDay, bucket, group_by: 'node' },
      { slug, node },
    )}`,
    { enabled: ready },
  );

  // useResource keeps the previous scope's data on an error, and keeps the
  // previous window's data while the request is disabled (an invalid custom
  // range). Drop it in both cases, so coverage, the truncated callout and the
  // CSV buttons never describe a window other than the one on screen.
  const data = !ready || report.error ? null : report.data;
  const dateText = rangeText(fromDay, toDay);
  const live = useMemo(
    () => new Map((liveNodes ?? []).map((n) => [n.node_uuid, n])),
    [liveNodes],
  );

  const nodes = useMemo(
    // Worst availability first; a node with none recorded goes last, because
    // "not recorded" is not "bad" and must not sit where an outage belongs.
    () => [...(data?.nodes ?? [])].sort((a, b) => (a.availability ?? 2) - (b.availability ?? 2)),
    [data],
  );
  const shown = nodes.slice(0, MAX_LINES);
  const labels = useMemo(
    () => new Map((data?.nodes ?? []).map((n) => [n.node_uuid, n.label || n.node_uuid.slice(0, 8)])),
    [data],
  );

  const seriesOf = (pick: (p: EdgeHealthPoint) => number | null): Series[] => {
    const byKey = new Map<string, EdgeHealthPoint[]>();
    for (const point of data?.points ?? []) {
      const key = point.key ?? '';
      const list = byKey.get(key) ?? [];
      list.push(point);
      byKey.set(key, list);
    }
    return shown.map((n, index) => ({
      key: n.node_uuid,
      label: labels.get(n.node_uuid) ?? n.node_uuid,
      slot: index + 1,
      points: (byKey.get(n.node_uuid) ?? []).map((p) => ({
        at: dayAsInstant(p.bucket),
        value: pick(p),
      })),
    }));
  };

  // Recomputed per render: three small maps over at most a few thousand points.
  const availabilitySeries = seriesOf((p) =>
    p.availability === null ? null : Math.round(p.availability * 1000) / 10,
  );
  const backlogSeries = seriesOf((p) => p.backlog_max);
  const diskSeries = seriesOf((p) => p.disk_free_min_bytes);
  const folded = nodes.length - shown.length;

  const exportNodes = () =>
    downloadCsv(
      `edge-health-${node || slug || 'fleet'}-nodes-${fromDay}-to-${toDay}.csv`,
      [
        'node_uuid',
        'label',
        'hospital',
        'center_slug',
        'agent_version',
        'recorded_from',
        'availability',
        'online_seconds',
        'expected_seconds',
        'heartbeats',
        'reboots',
        'orthanc_down_beats',
        'backlog_max',
        'backlog_last',
        'disk_free_min_bytes',
        'disk_total_bytes',
        'disk_free_min_pct',
        'days_to_full',
        'full_on',
        'forecast_reason',
      ],
      nodes.map((n): CsvCell[] => [
        n.node_uuid,
        n.label,
        n.hospital,
        n.center_slug,
        n.agent_version,
        n.recorded_from,
        n.availability,
        n.online_seconds,
        n.expected_seconds,
        n.heartbeats,
        n.reboots,
        n.orthanc_down_beats,
        n.backlog_max,
        n.backlog_last,
        n.disk_free_min_bytes,
        n.disk_total_bytes,
        n.disk_free_min_pct,
        n.forecast.days_to_full,
        n.forecast.full_on,
        n.forecast.reason,
      ]),
    );

  const exportPoints = () => {
    if (!data) return;
    downloadCsv(
      `edge-health-${node || slug || 'fleet'}-series-${fromDay}-to-${toDay}.csv`,
      [
        'bucket',
        'node_uuid',
        'label',
        'availability',
        'online_seconds',
        'expected_seconds',
        'heartbeats',
        'reboots',
        'orthanc_down_beats',
        'backlog_max',
        'backlog_last',
        'disk_free_min_bytes',
        'disk_total_bytes',
        'partial',
      ],
      data.points.map((p): CsvCell[] => [
        p.bucket,
        p.key,
        p.key ? labels.get(p.key) ?? p.key : null,
        p.availability,
        p.online_seconds,
        p.expected_seconds,
        p.heartbeats,
        p.reboots,
        p.orthanc_down_beats,
        p.backlog_max,
        p.backlog_last,
        p.disk_free_min_bytes,
        p.disk_total_bytes,
        p.partial,
      ]),
    );
  };

  const csvButton = (onClick: () => void, what: string, disabled: boolean) => (
    <Button kind="ghost" size="sm" renderIcon={DownloadIcon} onClick={onClick} disabled={disabled}>
      CSV<span className="visually-hidden"> of {what}</span>
    </Button>
  );

  const body = (render: () => ReactNode): ReactNode => {
    if (!ready) return <p className="viz__empty">Choose a valid window.</p>;
    if (report.loading) return <LoadingBlock label="Loading edge node health…" />;
    if (report.error instanceof ApiError && report.error.status === 409)
      return (
        <p className="viz__empty">
          {report.error.detail} — no edge node can be linked to this center.
        </p>
      );
    if (report.error)
      return <ErrorBlock what="edge node health" error={report.error} onRetry={report.reload} />;
    return render();
  };

  const coverage = data ? (
    <>
      {data.coverage.earliest
        ? <>Recorded by the cloud ingest from {data.coverage.earliest}; earlier days are not recorded, not down.</>
        : 'Nothing recorded yet.'}
      {data.coverage.reason ? <> {data.coverage.reason}</> : null}
      {folded > 0 ? <> Showing the {MAX_LINES} least available of {formatNumber(nodes.length)} nodes; the table lists all.</> : null}
    </>
  ) : undefined;
  const unavailable = data?.unavailable_reason || undefined;

  return (
    <>
      <Card className="bento__full" aria-labelledby="edge-health-heading">
        <div className="card__body stack">
          <div>
            <h2 className="section__title" id="edge-health-heading">
              Edge node health
            </h2>
            <p className="muted">
              Availability as seen by the cloud ingest — the share of each node&rsquo;s time
              in which its heartbeats arrived — by Baghdad day, {dateText}.
              {note ? <> {note}</> : null}
            </p>
          </div>

          {data?.truncated ? (
            <Callout tone="warning" title="Edge health series truncated">
              Only the most recent buckets are shown
              {data.points[0] ? <> — from {data.points[0].bucket}</> : null}. The oldest part
              of the window is missing, not empty. Choose a coarser bucket or a shorter window.
            </Callout>
          ) : null}

          <ChartFrame
            title="By edge node"
            subtitle="Least available first. Backlog is studies on the node and nowhere else — the number that is data at risk. The forecast is a straight line through the lowest daily free disk; Orthanc's storage cap may recycle old studies before then."
            unavailable={unavailable}
            coverage={coverage}
          >
            {body(() =>
              nodes.length === 0 ? (
                <p className="viz__empty">No edge node is enrolled in this scope.</p>
              ) : (
                <TableScroll label="Edge node health">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th scope="col">Node</th>
                        <th scope="col" className="numeric">Availability</th>
                        <th scope="col" className="numeric">Reboots</th>
                        <th scope="col" className="numeric">Orthanc down</th>
                        <th scope="col" className="numeric">Backlog worst</th>
                        <th scope="col" className="numeric">Backlog now</th>
                        <th scope="col">Last replication</th>
                        <th scope="col" className="numeric">Lowest disk free</th>
                        <th scope="col">Disk forecast</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nodes.map((n) => {
                        const now = live.get(n.node_uuid);
                        const backlogNow = now ? now.unreplicated_count : n.backlog_last;
                        return (
                          <tr key={n.node_uuid}>
                            <td>
                              <Link to={`/edge-nodes/${encodeURIComponent(n.node_uuid)}`}>
                                {n.label || n.node_uuid}
                              </Link>
                              {n.center_slug ? <span className="muted"> · {n.center_slug}</span> : null}
                              {n.recorded_from ? (
                                <span className="muted"> · recorded from {n.recorded_from}</span>
                              ) : null}
                            </td>
                            <td className="numeric" style={{ color: availabilityColor(n.availability) }}>
                              {percent(n.availability)}
                            </td>
                            <td className="numeric">{formatNumber(n.reboots)}</td>
                            <td className="numeric" title="Heartbeats that reported Orthanc down">
                              {formatNumber(n.orthanc_down_beats)}
                            </td>
                            <td className="numeric">{formatNumber(n.backlog_max)}</td>
                            <td className="numeric">
                              {backlogNow !== null && backlogNow !== undefined && backlogNow > 0 ? (
                                <strong>{formatNumber(backlogNow)}</strong>
                              ) : (
                                formatNumber(backlogNow)
                              )}
                            </td>
                            <td>
                              {now?.last_replication_success_at ? (
                                <time
                                  dateTime={now.last_replication_success_at}
                                  title={formatDateTime(now.last_replication_success_at)}
                                >
                                  {formatRelative(now.last_replication_success_at)}
                                </time>
                              ) : (
                                <span className="muted">{UNKNOWN}</span>
                              )}
                            </td>
                            <td className="numeric">{diskText(n.disk_free_min_bytes, n.disk_free_min_pct)}</td>
                            <td>{forecastText(n)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              ),
            )}
          </ChartFrame>
          <div className="row">
            <span className="push">
              {csvButton(exportPoints, 'the edge health series', !data)}{' '}
              {csvButton(exportNodes, 'the edge health table', nodes.length === 0)}
            </span>
          </div>
        </div>
      </Card>

      <Card className="bento__third">
        <div className="card__body">
          <ChartFrame
            title="Edge availability"
            subtitle={`Percent of each ${bucket} the ingest heard the node. A gap is not recorded, not down.`}
            series={availabilitySeries}
            unavailable={unavailable}
          >
            {body(() => (
              <LineChart
                series={availabilitySeries}
                label="Edge node availability over time"
                format={(value) => `${value.toFixed(1)}%`}
                fill={availabilitySeries.length === 1}
              />
            ))}
          </ChartFrame>
        </div>
      </Card>

      <Card className="bento__third">
        <div className="card__body">
          <ChartFrame
            title="Edge backlog"
            subtitle={`Worst unreplicated count each ${bucket}: studies at the hospital and nowhere else.`}
            series={backlogSeries}
            unavailable={unavailable}
          >
            {body(() => (
              <LineChart
                series={backlogSeries}
                label="Worst replication backlog per node over time"
                format={(value) => formatNumber(Math.round(value))}
                fill={backlogSeries.length === 1}
              />
            ))}
          </ChartFrame>
        </div>
      </Card>

      <Card className="bento__third">
        <div className="card__body">
          <ChartFrame
            title="Edge disk free"
            subtitle={`Lowest free disk each ${bucket}.`}
            series={diskSeries}
            unavailable={unavailable}
          >
            {body(() => (
              <LineChart
                series={diskSeries}
                label="Lowest free disk per node over time"
                format={(value) => formatBytes(value)}
                fill={diskSeries.length === 1}
              />
            ))}
          </ChartFrame>
        </div>
      </Card>
    </>
  );
}
